import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CardLink } from '../../../components/CardLink';
import {
    createMilestone, updateMilestone, deleteMilestone, getTasks,
    type Project, type Milestone, type Task, type ProjectProgress, type StatusCount, type ProjectMember,
} from '../../../packages/api-client/pm-api';
import { getErrorMessage } from '../../../packages/api-client';
import { MemberList, MetricStrip, TaskPanel, memberName } from './dashboardParts';
import { formatDueDate } from '../../../utils/date';
import { StatusIcon, MilestoneStatusIcon } from '../../../components/taskIcons';
import { Plus, Calendar, Flag, ChevronRight, Trash2, X } from 'lucide-react';

const PROJECT_STATUS: Record<Project['status'], string> = {
    planning: 'Planning', active: 'Active', on_hold: 'On hold', completed: 'Completed', archived: 'Archived',
};

// State for the Overview section. Called unconditionally by ProjectDetailPage
// so the state outlives tab switches exactly as it did when it lived inline.
export function useOverviewSection(
    id: string | undefined,
    setMilestones: React.Dispatch<React.SetStateAction<Milestone[]>>,
) {
    // Expandable tasks-under-milestone on the Overview tab. Lazy-fetched
    // the first time a milestone is expanded; cached in state so a
    // collapse-then-expand cycle doesn't refetch.
    const [expandedMilestoneIds, setExpandedMilestoneIds] = useState<Set<string>>(new Set());
    const [milestoneTasks, setMilestoneTasks] = useState<Record<string, Task[]>>({});
    const [loadingMilestoneTasks, setLoadingMilestoneTasks] = useState<Set<string>>(new Set());
    const [showAddMilestone, setShowAddMilestone] = useState(false);
    const [msForm, setMsForm] = useState({ name: '', description: '', due_date: '', status: 'upcoming' });
    const [addingMs, setAddingMs] = useState(false);

    const MS_STATUSES: Milestone['status'][] = ['upcoming', 'in_progress', 'completed', 'overdue'];
    async function handleAddMilestone(e: React.FormEvent) {
        e.preventDefault();
        if (!id || !msForm.name.trim()) return;
        setAddingMs(true);
        try {
            const ms = await createMilestone(id, {
                name: msForm.name, description: msForm.description,
                due_date: msForm.due_date || undefined, status: msForm.status as Milestone['status'],
            });
            setMilestones(prev => [ms, ...prev]);
            setMsForm({ name: '', description: '', due_date: '', status: 'upcoming' });
            setShowAddMilestone(false);
        } finally { setAddingMs(false); }
    }
    async function handleDeleteMilestone(msId: string) {
        if (!confirm('Delete this milestone?')) return;
        await deleteMilestone(msId);
        setMilestones(prev => prev.filter(m => m.id !== msId));
    }
    async function handleCycleStatus(ms: Milestone) {
        const next = MS_STATUSES[(MS_STATUSES.indexOf(ms.status) + 1) % MS_STATUSES.length];
        const updated = await updateMilestone(ms.id, { status: next });
        setMilestones(prev => prev.map(m => m.id === ms.id ? updated : m));
    }

    async function toggleMilestoneExpand(milestoneID: string) {
        // Collapse: just remove from the set.
        if (expandedMilestoneIds.has(milestoneID)) {
            setExpandedMilestoneIds(prev => {
                const next = new Set(prev);
                next.delete(milestoneID);
                return next;
            });
            return;
        }
        // Expand: mark expanded immediately so the user gets feedback, then
        // lazy-fetch tasks the first time. Subsequent expansions reuse the
        // cached list — a stale view is fine here because the Overview tab
        // is a snapshot, not the source of truth (Tasks tab is).
        setExpandedMilestoneIds(prev => new Set(prev).add(milestoneID));
        if (milestoneTasks[milestoneID] || loadingMilestoneTasks.has(milestoneID) || !id) return;
        setLoadingMilestoneTasks(prev => new Set(prev).add(milestoneID));
        try {
            // flat=true so the milestone_id filter applies across every task,
            // not just parents. Without this the expansion misses any subtask
            // explicitly tagged with this milestone — but the progress badge
            // (LEFT JOIN tasks in milestones.Repository.List) counts ALL
            // matching rows, so "3/7" with an empty expansion happens whenever
            // some of those rows are subtasks.
            const res = await getTasks(id, { milestone_id: milestoneID, flat: 'true', limit: '100' });
            setMilestoneTasks(prev => ({ ...prev, [milestoneID]: res.data ?? [] }));
        } catch {
            // Leave the entry unset so a retry-on-reopen kicks off another fetch.
        } finally {
            setLoadingMilestoneTasks(prev => {
                const next = new Set(prev);
                next.delete(milestoneID);
                return next;
            });
        }
    }

    return {
        expandedMilestoneIds, milestoneTasks, loadingMilestoneTasks,
        showAddMilestone, setShowAddMilestone, msForm, setMsForm, addingMs,
        handleAddMilestone, handleDeleteMilestone, handleCycleStatus, toggleMilestoneExpand,
    };
}

/**
 * The tasks behind the Overview's task panel: review + blocked ("Next
 * decisions") for full access, the first page of visible tasks for a
 * restricted user ("Your shared tasks"). Both are scoped server-side.
 */
function useOverviewTasks(projectId: string, restricted: boolean): Task[] | null | string {
    const [tasks, setTasks] = useState<Task[] | null | string>(null);
    useEffect(() => {
        let live = true;
        setTasks(null);
        const load = restricted
            ? getTasks(projectId, { limit: '10' }).then(r => r.data ?? [])
            : Promise.all(['review', 'blocked'].map(status => getTasks(projectId, { status, flat: 'true', limit: '10' })))
                .then(pages => pages.flatMap(p => p.data ?? []));
        load.then(t => { if (live) setTasks(t); })
            .catch(e => { if (live) setTasks(`Tasks couldn't be loaded: ${getErrorMessage(e)}`); });
        return () => { live = false; };
    }, [projectId, restricted]);
    return tasks;
}

/** The next milestone: the earliest-due one that is not completed (undated last). */
function nextMilestone(milestones: Milestone[]): Milestone | undefined {
    return milestones.filter(m => m.status !== 'completed')
        .sort((a, b) => (a.due_date ?? '\uffff').localeCompare(b.due_date ?? '\uffff'))[0];
}

/**
 * Project › Overview (founder concept renderProject 'overview'): metric strip,
 * then objective + next decisions + milestones on the left, next milestone,
 * team and repository on the right. A restricted user gets the scope notice,
 * their visible counts and their shared tasks.
 */
export function OverviewSection({ project, progress, statusBreakdown, milestones, members, restricted, taskRestricted, repo, link, state }: {
    project: Project;
    progress: ProjectProgress | null;
    statusBreakdown: StatusCount[];
    milestones: Milestone[];
    members: ProjectMember[];
    restricted: boolean;
    taskRestricted: boolean;
    repo: { label: string; href?: string } | null;
    /** href of another project section, keeping the page's other params. */
    link: (tab: string, view?: string) => string;
    state: ReturnType<typeof useOverviewSection>;
}) {
    const {
        expandedMilestoneIds, milestoneTasks, loadingMilestoneTasks,
        showAddMilestone, setShowAddMilestone, msForm, setMsForm, addingMs,
        handleAddMilestone, handleDeleteMilestone, handleCycleStatus, toggleMilestoneExpand,
    } = state;
    const tasks = useOverviewTasks(project.id, restricted);
    const owner = members.find(m => m.user_id === project.owner_id);
    const next = nextMilestone(milestones);
    const pct = Math.round(progress?.percent ?? 0);
    return (
                <div className="pd-overview">
                    {restricted && (
                        <p className="pd-scope-notice">
                            This view includes only the tasks shared with you. Project totals you can&apos;t access are not shown.
                        </p>
                    )}
                    <MetricStrip breakdown={statusBreakdown} full={!restricted} />
                    <div className="pd-columns">
                    <div className="pd-stack">
                    {!restricted && (
                        <section className="pd-panel pd-panel--block" aria-labelledby="pd-objective">
                            <span id="pd-objective" className="pd-eyebrow">Project objective</span>
                            {project.description
                                ? <h2 className="pd-goal">{project.description}</h2>
                                : <>
                                    <h2 className="pd-goal pd-goal--empty">No objective written yet.</h2>
                                    <Link className="pd-link" to={link('Tools', 'settings')}>Add a description in project settings ↗</Link>
                                </>}
                            <div className="pd-identity">
                                {owner && <span className="pd-pill">Owner: {memberName(owner)}</span>}
                                {project.due_date && <span className="pd-pill">Target: {formatDueDate(project.due_date)}</span>}
                                <span className="pd-pill">{PROJECT_STATUS[project.status]}</span>
                                {!!progress?.overdue_tasks && <span className="pd-pill pd-pill--blocked">{progress.overdue_tasks} overdue</span>}
                            </div>
                            {progress && (
                                <div className="pd-progress">
                                    <div className="pd-progress-row">
                                        <span>{progress.done_tasks} of {progress.total_tasks} tasks completed</span>
                                        <strong>{pct}%</strong>
                                    </div>
                                    <div className="pd-track" role="progressbar" aria-label="Project completion" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
                                        <span style={{ width: `${pct}%`, background: project.color || undefined }} />
                                    </div>
                                </div>
                            )}
                        </section>
                    )}

                    {restricted
                        ? <TaskPanel title="Your shared tasks" tasks={tasks} members={members} empty="No tasks are currently shared with you."
                            action={<Link className="pd-link" to={link('Tasks')}>All shared tasks ↗</Link>} />
                        : <TaskPanel title="Next decisions" tasks={tasks} members={members} empty="No outstanding decisions: nothing is in review or blocked." />}

                    {/* Milestones: expandable rows (tasks lazy-loaded), cycle status,
                        add and delete. Hidden for task-restricted users. */}
                    {!taskRestricted && (
                    <section className="pd-panel" aria-labelledby="pd-milestones">
                        <div className="pd-panel-heading">
                            <h2 id="pd-milestones">Milestones</h2>
                            <span className="pd-count">{milestones.length}</span>
                            <button className="btn btn-ghost btn-sm" onClick={() => setShowAddMilestone(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}><Plus size={14} aria-hidden="true" /> Add milestone</button>
                        </div>
                        <div className="pd-milestone-list">
                        {milestones.length === 0
                            ? <p className="pd-panel-empty" style={{ paddingLeft: 0 }}>No milestones yet — add your first checkpoint.</p>
                            : milestones.map(m => {
                                const expanded = expandedMilestoneIds.has(m.id);
                                const ms_tasks = milestoneTasks[m.id];
                                const ms_loading = loadingMilestoneTasks.has(m.id);
                                return (
                                    <React.Fragment key={m.id}>
                                        <div
                                            onClick={() => toggleMilestoneExpand(m.id)}
                                            style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-3)', padding: 'var(--space-3) 0', borderBottom: expanded ? 'none' : '1px solid var(--color-border)', cursor: 'pointer' }}>
                                            <span aria-hidden style={{ width: 12, color: 'var(--color-text-muted)', display: 'inline-flex', alignItems: 'center', transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'none', flexShrink: 0 }}><ChevronRight size={12} /></span>
                                            <button title="Cycle status" aria-label="Cycle milestone status" onClick={e => { e.stopPropagation(); handleCycleStatus(m); }}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, lineHeight: 1, padding: 0, minWidth: 32, minHeight: 32, margin: '-7px 0' }}>
                                                <MilestoneStatusIcon status={m.status} size={18} />
                                            </button>
                                            <div style={{ flex: 1, minWidth: '140px' }}>
                                                <div style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>{m.name}</div>
                                                {m.description && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{m.description}</div>}
                                            </div>
                                            {m.due_date && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Calendar size={12} aria-hidden="true" /> {formatDueDate(m.due_date)}</span>}
                                            {/* Progress badge from the milestones LEFT JOIN counts.
                                                Render only when the milestone has tasks — empty milestones
                                                show 0/0 which is noise. The color is a coarse signal: green
                                                once all tasks are done, gray otherwise. */}
                                            {typeof m.task_count === 'number' && m.task_count > 0 && (
                                                <span style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: m.done_count === m.task_count ? 'var(--color-success)' : 'var(--color-text-muted)',
                                                    fontWeight: 600, flexShrink: 0,
                                                }} title={`${m.done_count ?? 0} of ${m.task_count} tasks done`}>
                                                    {m.done_count ?? 0}/{m.task_count}
                                                </span>
                                            )}
                                            <span className={`status-badge status-${m.status}`} style={{ flexShrink: 0 }}>{m.status.replace('_', ' ')}</span>
                                            <button className="btn btn-ghost btn-sm" aria-label="Delete milestone" title="Delete milestone" style={{ color: 'var(--color-danger)', flexShrink: 0 }} onClick={e => { e.stopPropagation(); handleDeleteMilestone(m.id); }}><Trash2 size={14} aria-hidden="true" /></button>
                                        </div>
                                        {expanded && (
                                            <div style={{ padding: 'var(--space-2) 0 var(--space-3) calc(12px + var(--space-3) + 1.1rem + var(--space-3))', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-2)', marginBottom: 0 }}>
                                                {ms_loading ? (
                                                    <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-3)' }}><div className="spinner" /></div>
                                                ) : !ms_tasks || ms_tasks.length === 0 ? (
                                                    <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', margin: 0, padding: 'var(--space-2) 0' }}>No tasks attached to this milestone yet.</p>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                                                        {ms_tasks.map(t => (
                                                            <CardLink key={t.id} to={`/tasks/${t.id}`} ariaLabel={t.title}
                                                                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: '4px 0', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
                                                                <span style={{ width: 16, display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}><StatusIcon status={t.status} /></span>
                                                                <span style={{ flex: 1, textDecoration: t.status === 'done' ? 'line-through' : 'none', color: t.status === 'done' ? 'var(--color-text-muted)' : 'inherit' }}>{t.title}</span>
                                                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'capitalize', flexShrink: 0 }}>{t.status.replace('_', ' ')}</span>
                                                            </CardLink>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        }
                    </div>
                    </section>
                    )}
                    </div>

                    <div className="pd-stack">
                    {!taskRestricted && (
                        <section className="pd-panel pd-panel--block" aria-labelledby="pd-next-milestone">
                            <span id="pd-next-milestone" className="pd-eyebrow">Next milestone</span>
                            {next ? <>
                                <h3>{next.name}</h3>
                                <p>{next.due_date ? `Target ${formatDueDate(next.due_date)} · ` : ''}{next.status.replace('_', ' ')}</p>
                            </> : <p style={{ marginTop: 'var(--space-3)' }}>No open milestone.</p>}
                            {!restricted && <Link className="btn btn-secondary btn-sm" to={link('Planning')}>Open project plan ↗</Link>}
                        </section>
                    )}
                    {!restricted && (
                        <section className="pd-panel pd-panel--block" aria-labelledby="pd-team">
                            <span id="pd-team" className="pd-eyebrow">Project team</span>
                            {members.length ? <MemberList members={members} ownerId={project.owner_id} /> : <p style={{ marginTop: 'var(--space-3)' }}>No members yet.</p>}
                            <Link className="pd-link" to={link('Members')}>Members &amp; access ↗</Link>
                        </section>
                    )}
                    {!restricted && repo && (
                        <section className="pd-panel pd-panel--block" aria-labelledby="pd-repo">
                            <span id="pd-repo" className="pd-eyebrow">Repository &amp; agents</span>
                            <h3>{repo.href ? <a href={repo.href} target="_blank" rel="noopener noreferrer">{repo.label}</a> : <span>{repo.label}</span>}</h3>
                            <p>Agent handoffs and evidence stay on their tasks.</p>
                            <Link className="pd-link" to="/connections">Your connections ↗</Link>
                        </section>
                    )}
                    </div>
                    </div>

                    {/* Add Milestone Modal */}
                    {showAddMilestone && (
                        <div className="modal-overlay" onClick={() => setShowAddMilestone(false)}>
                            <div className="modal" onClick={e => e.stopPropagation()}>
                                <div className="modal-header">
                                    <span className="modal-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}><Flag size={16} aria-hidden="true" /> New Milestone</span>
                                    <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setShowAddMilestone(false)}><X size={16} aria-hidden="true" /></button>
                                </div>
                                <form onSubmit={handleAddMilestone}>
                                    <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <div className="form-group">
                                            <label className="form-label">Name *</label>
                                            <input className="form-input" required autoFocus placeholder="e.g. Beta Release"
                                                value={msForm.name} onChange={e => setMsForm(f => ({ ...f, name: e.target.value }))} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Description</label>
                                            <input className="form-input" placeholder="Optional notes"
                                                value={msForm.description} onChange={e => setMsForm(f => ({ ...f, description: e.target.value }))} />
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div className="form-group">
                                                <label className="form-label">Due Date</label>
                                                <input className="form-input" type="date"
                                                    value={msForm.due_date} onChange={e => setMsForm(f => ({ ...f, due_date: e.target.value }))} />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Status</label>
                                                <select className="form-input" value={msForm.status} onChange={e => setMsForm(f => ({ ...f, status: e.target.value }))}>
                                                    <option value="upcoming">Upcoming</option>
                                                    <option value="in_progress">In Progress</option>
                                                    <option value="completed">Completed</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button className="btn btn-secondary btn-md" type="button" onClick={() => setShowAddMilestone(false)}>Cancel</button>
                                        <button className="btn btn-primary btn-md" type="submit" disabled={addingMs}>{addingMs ? 'Creating…' : 'Create Milestone'}</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
    );
}
